'use client'

import type { ReactNode } from 'react'
import Link from 'next/link'
import { Card, BlockStack, Text } from '@shopify/polaris'
import { useSigmaSurface } from '@/components/SigmaSurfaceProvider'
import type { DashboardMoversResult } from '@/lib/dashboard/dashboardMovers'
import {
  getDashboardMoverDrilldownHref,
  type DashboardMoverSeriesId,
} from '@/lib/dashboard/dashboardMoverDrilldownUrls'
import styles from './DashboardMoversSummary.module.css'

/** Bold numeric / currency segments for portal readability */
function emphasizePortalNumbers(text: string): ReactNode {
  const re = /(Rs\.?\s*[\d,]+\.\d{2}|\(\s*[+\-−]?\d[\d,.]*%\s*\)|[+\-−]?\d[\d,.]*%|[+\-−]\s*[\d,.]+)/g
  const nodes: ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  const r = new RegExp(re.source, 'g')
  while ((m = r.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    nodes.push(
      <strong key={`${m.index}-${m[0]}`} className={styles.moverFigure}>
        {m[0]}
      </strong>
    )
    last = m.index + m[0].length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes.length > 0 ? <>{nodes}</> : text
}

const ARIA_LABEL_BY_SERIES: Record<DashboardMoverSeriesId, string> = {
  total_orders: 'Open total orders list for the selected dashboard dates',
  delivered_orders: 'Open delivered orders for the selected dashboard dates',
  returned_orders: 'Open returned orders for the selected dashboard dates',
  total_revenue_all: 'Open total revenue (all) details for the selected dashboard dates',
  delivered_revenue: 'Open delivered revenue details for the selected dashboard dates',
  total_expenses: 'Open total expenses breakdown for the selected dashboard dates',
  total_profit: 'Open total profit breakdown for the selected dashboard dates',
}

function MoverBulletLine(props: {
  seriesId: DashboardMoverSeriesId
  text: string
  shop: string | null
  host: string | null
  dateFrom: string
  dateTo: string
  bodyVariant: 'bodySm' | 'bodyMd'
  emphasizeNumbers?: boolean
}) {
  const { seriesId, text, shop, host, dateFrom, dateTo, bodyVariant, emphasizeNumbers } = props
  const href = getDashboardMoverDrilldownHref(seriesId, shop, host, dateFrom, dateTo)
  const content = emphasizeNumbers ? emphasizePortalNumbers(text) : text

  if (!href) {
    return (
      <Text as="span" variant={bodyVariant} fontWeight={emphasizeNumbers ? 'medium' : undefined}>
        {content}
      </Text>
    )
  }

  return (
    <Link href={href} className={styles.moverLink} aria-label={ARIA_LABEL_BY_SERIES[seriesId]}>
      <Text as="span" variant={bodyVariant} fontWeight={emphasizeNumbers ? 'medium' : undefined}>
        {content}
      </Text>
    </Link>
  )
}

export function DashboardMoversSummary(props: {
  movers: DashboardMoversResult
  shop: string | null
  host: string | null
  dateFrom: string
  dateTo: string
  /** Cap list length per section (default: all). */
  maxBulletsPerSection?: number
}) {
  const { movers, shop, host, dateFrom, dateTo, maxBulletsPerSection } = props
  const surface = useSigmaSurface()
  const isPortal = surface === 'portal'
  const bodyVariant = isPortal ? 'bodyMd' : 'bodySm'

  if (!movers.hasComparablePeriod) return null
  if (movers.improvements.length === 0 && movers.setbacks.length === 0) return null

  const imp = maxBulletsPerSection != null ? movers.improvements.slice(0, maxBulletsPerSection) : movers.improvements
  const set = maxBulletsPerSection != null ? movers.setbacks.slice(0, maxBulletsPerSection) : movers.setbacks

  const card = (
    <Card>
      <BlockStack gap="200">
        <Text as="h2" variant={isPortal ? 'headingLg' : 'headingMd'}>
          Biggest moves vs previous period
        </Text>
        {movers.improvements.length > 0 ? (
          <BlockStack gap="100">
            <Text as="p" variant="bodyMd" fontWeight="semibold" tone="success">
              Best improvements
            </Text>
            <ul className={isPortal ? styles.improvementList : undefined} style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {imp.map((bullet, i) => (
                <li key={`imp-${bullet.seriesId}-${i}`}>
                  <MoverBulletLine
                    seriesId={bullet.seriesId}
                    text={bullet.text}
                    shop={shop}
                    host={host}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    bodyVariant={bodyVariant}
                    emphasizeNumbers={isPortal}
                  />
                </li>
              ))}
            </ul>
          </BlockStack>
        ) : null}
        {movers.setbacks.length > 0 ? (
          <BlockStack gap="100">
            <Text as="p" variant="bodyMd" fontWeight="semibold" tone="critical">
              Largest setbacks
            </Text>
            <ul className={isPortal ? styles.setbackList : undefined} style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {set.map((bullet, i) => (
                <li key={`set-${bullet.seriesId}-${i}`}>
                  <MoverBulletLine
                    seriesId={bullet.seriesId}
                    text={bullet.text}
                    shop={shop}
                    host={host}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    bodyVariant={bodyVariant}
                    emphasizeNumbers={isPortal}
                  />
                </li>
              ))}
            </ul>
          </BlockStack>
        ) : null}
      </BlockStack>
    </Card>
  )

  if (isPortal) {
    return <div className="sigma-portal-movers-wrap">{card}</div>
  }
  return card
}
